/**
 * The main menu's favourites and usage tracking, persisted so they survive an app
 * kill/restart (Android is aggressive about killing a backgrounded app). Two
 * things live here:
 *
 *  - favIds: the manual favourites strip, in bar order (left to right, sitting to
 *    the RIGHT of the auto most-used slot). Seeded with the original default set
 *    so the bar is never bare, and kept to a fixed width: adding a favourite
 *    inserts it at the FRONT and drops the RIGHT-MOST one, so the strip stays the
 *    same length. These are menu leaf ids for anything added from the menu, plus
 *    the `fav-*` default pseudo-ids (see DEFAULT_FAV_IDS) for the built-ins.
 *  - usage: how many times each feature has been opened. The bar pins the single
 *    most-used feature that ISN'T already a favourite at the far left, so the one
 *    tool we reach for most is always the first tap even if we never starred it,
 *    and it shuffles as our habits change.
 *
 * Keyed on the menu leaf id (a stable string on every feature), following the
 * chart-provider favourites pattern. One localStorage object holds both.
 */
import { create } from 'zustand';
import { loadJson, saveJson } from './persist';

const KEY = 'acp.menuFavourites.v1';

/**
 * The original default favourites: the fixed strip of quick-access icons the bar
 * shows out of the box (design/chartplotter-menu-structure.md — Tidal, Go to,
 * Route, Anchor, Alarms). Held as their own `fav-*` pseudo-ids, matching
 * MENU_TREE.favourites, so the bar renders the exact original icon and label for
 * each. That matters most for Tidal, which opens the Tides sheet directly and has
 * no standalone menu leaf, so it can't be looked up through findMenuLeaf the way
 * the other four can; keeping it as a pseudo-id is what stops it being silently
 * dropped from the strip.
 */
export const DEFAULT_FAV_IDS = [
  'fav-tidal',
  'fav-goto',
  'fav-route',
  'fav-anchor',
  'fav-alarms',
] as const;

/**
 * The underlying menu leaf id for each default that has one, so the auto
 * most-used slot never surfaces a feature a default already covers (e.g. the
 * Route default and the Routes leaf are the same thing to the skipper). Tidal has
 * no leaf, so it's absent here on purpose.
 */
export const DEFAULT_FAV_LEAF: Record<string, string> = {
  'fav-goto': 'go-to',
  'fav-route': 'route-library',
  'fav-anchor': 'anchor-watch',
  'fav-alarms': 'alarm-panel',
};

/**
 * How many manual favourites the strip holds (to the right of the single auto
 * most-used slot). Matches the original default set's width, so the full built-in
 * set fits and adding one pushes the right-most off rather than growing the bar.
 */
export const MANUAL_FAV_SLOTS = DEFAULT_FAV_IDS.length;

interface Persisted {
  favIds: string[];
  usage: Record<string, number>;
}

const DEFAULT: Persisted = { favIds: [...DEFAULT_FAV_IDS], usage: {} };

function load(): Persisted {
  const p = loadJson<Persisted>(KEY, DEFAULT);
  const favIds = Array.isArray(p.favIds)
    ? p.favIds.filter((x): x is string => typeof x === 'string')
    : [];
  return {
    // An empty list means a fresh install, or an early build that persisted [];
    // either way seed the original defaults so the strip is never bare.
    favIds: favIds.length ? favIds.slice(0, MANUAL_FAV_SLOTS) : [...DEFAULT_FAV_IDS],
    usage: p.usage && typeof p.usage === 'object' && !Array.isArray(p.usage) ? p.usage : {},
  };
}

interface MenuFavouritesState extends Persisted {
  isFavourite: (id: string) => boolean;
  /**
   * Add a feature to the manual favourites strip: insert it at the FRONT (just
   * right of the auto most-used slot), drop any existing copy first so it's never
   * duplicated, then cap to MANUAL_FAV_SLOTS so the right-most icon falls off the
   * end. The bar width stays constant: one in on the left, one off the right.
   */
  addFavourite: (id: string) => void;
  /** Pin (front-insert, same as addFavourite) or unpin a feature by its id. */
  toggleFavourite: (id: string) => void;
  /** Count one opening of a feature, for the auto most-used slot. */
  recordUse: (id: string) => void;
  /**
   * The `n` most-used feature ids that aren't in `exclude` (the current
   * favourites), most-used first. Ties break by id so the order is stable.
   */
  mostUsed: (n: number, exclude: readonly string[]) => string[];
}

/** Front-insert `id`, drop any existing copy, cap to the fixed strip width. */
function insertFront(favIds: readonly string[], id: string): string[] {
  return [id, ...favIds.filter((x) => x !== id)].slice(0, MANUAL_FAV_SLOTS);
}

export const useMenuFavouritesStore = create<MenuFavouritesState>((set, get) => ({
  ...load(),
  isFavourite: (id) => get().favIds.includes(id),
  addFavourite: (id) =>
    set((s) => {
      const favIds = insertFront(s.favIds, id);
      saveJson(KEY, { favIds, usage: s.usage });
      return { favIds };
    }),
  toggleFavourite: (id) =>
    set((s) => {
      const favIds = s.favIds.includes(id)
        ? s.favIds.filter((x) => x !== id)
        : insertFront(s.favIds, id);
      saveJson(KEY, { favIds, usage: s.usage });
      return { favIds };
    }),
  recordUse: (id) =>
    set((s) => {
      const usage = { ...s.usage, [id]: (s.usage[id] ?? 0) + 1 };
      saveJson(KEY, { favIds: s.favIds, usage });
      return { usage };
    }),
  mostUsed: (n, exclude) => mostUsedFrom(get().usage, n, exclude),
}));

/**
 * The `n` most-used feature ids from a usage map that aren't in `exclude`,
 * most-used first, ties broken by id so the order is stable. Kept as a pure
 * function (not just the store method) so a component can derive it inside a
 * useMemo from the `usage`/`favIds` store slices, rather than building a fresh
 * array inside a zustand selector, which would hand useSyncExternalStore an
 * unstable snapshot and throw on every render.
 */
export function mostUsedFrom(
  usage: Record<string, number>,
  n: number,
  exclude: readonly string[],
): string[] {
  if (n <= 0) return [];
  const skip = new Set(exclude);
  return Object.entries(usage)
    .filter(([id, count]) => count > 0 && !skip.has(id))
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, n)
    .map(([id]) => id);
}
